'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export default function ProductQuestionsSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const faqs: FAQItem[] = [
    {
      id: 'product_1',
      question: 'What is the difference between Layer and Broiler concentrates?',
      answer: 'Layer concentrate is formulated to maximize egg production, improve shell strength, and maintain consistent laying cycles. Broiler concentrate is designed for rapid weight gain, improved feed conversion ratio, and optimal muscle development. Each product has a specific nutrient profile tailored to the unique needs of layers or broilers.'
    },
    {
      id: 'product_2',
      question: 'How do I use Bloom concentrates?',
      answer: 'Bloom concentrates are designed to be mixed with locally available energy sources such as maize bran according to our provided feeding guidelines. Detailed mixing ratios and instructions are included with each purchase. Our technical team is also available to guide you through the proper usage.'
    },
    {
      id: 'product_3',
      question: 'What are the recommended mixing ratios?',
      answer: 'Mixing ratios vary depending on the bird type and production stage. For layers, we typically recommend a specific concentrate-to-energy source ratio that ensures optimal nutrient balance. For broilers, ratios are adjusted based on growth phases. Contact our technical support team for precise formulation guidance tailored to your farm.'
    },
    {
      id: 'product_4',
      question: 'What is the shelf life of Bloom concentrates?',
      answer: 'When stored properly in a cool, dry place away from direct sunlight and moisture, Bloom concentrates have a shelf life of 6 months from the manufacturing date. Always check the production date on the packaging and follow proper storage guidelines to maintain product quality.'
    },
    {
      id: 'product_5',
      question: 'How should I store the concentrate?',
      answer: 'Store Bloom concentrates in a cool, dry, well-ventilated area away from direct sunlight, moisture, and pests. Keep bags sealed when not in use and elevated off the ground on pallets. Avoid storing near chemicals or strong-smelling substances that could contaminate the feed.'
    }
  ];

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-section-sm px-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <h2 className="text-heading-3 text-foreground mb-4">Product Questions</h2>
          <p className="text-muted-foreground">
            Learn about our Layer and Broiler concentrates, usage instructions, and storage guidelines.
          </p>
        </div>

        <div className="space-y-4">
          {faqs?.map((faq) => (
            <div
              key={faq?.id}
              className="bg-white rounded-lg border border-border overflow-hidden transition-all hover:shadow-md"
            >
              <button
                onClick={() => toggleFAQ(faq?.id)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-secondary/5 transition-colors"
              >
                <span className="font-semibold text-foreground pr-4">{faq?.question}</span>
                <Icon
                  name={openId === faq?.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={20}
                  className="text-primary flex-shrink-0"
                />
              </button>
              {openId === faq?.id && (
                <div className="px-6 pb-5 text-muted-foreground leading-relaxed">
                  {faq?.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}